import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  ChessQueen,
  Gem,
  GraduationCap,
  Home,
  LogIn,
  ShieldCheck,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useExtracted, useLocale } from "next-intl";
import { localizedLearningPath } from "@/lib/public-learning-routes";
import { cn } from "@/lib/utils";
import Link from "@/components/compat/Link";
import { LogoIcon } from "@/components/ui/logo-icon";
import { Logotype } from "@/components/ui/logotype";
import { useMarketingNav } from "./MarketingNavProvider";

const EASE = [0.16, 1, 0.3, 1] as const;

export function MarketingNavDrawer() {
  const locale = useLocale();
  const learnHref = localizedLearningPath(
    "/learn",
    locale === "zh-CN" ? "zh-CN" : "en",
  );
  const te = useExtracted();
  const { pathname } = useLocation();
  const { open, closeNav } = useMarketingNav();

  const HOME_LABEL = te("Home");
  const ABOUT_LABEL = te("About");
  const LEARN_LABEL = te("Options Theory");
  const SAFETY_LABEL = te("Safety");
  const PURCHASE_LABEL = te("Purchase Access");
  const LOGIN_LABEL = te("Log in");
  const CLOSE_LABEL = te("Close menu");

  const navItems = [
    { href: "/", icon: Home, label: HOME_LABEL },
    { href: "/about", icon: ChessQueen, label: ABOUT_LABEL },
    { href: learnHref, icon: GraduationCap, label: LEARN_LABEL },
    { href: "/safety", icon: ShieldCheck, label: SAFETY_LABEL },
    { href: "/purchase", icon: Gem, label: PURCHASE_LABEL },
    { href: "/login", icon: LogIn, label: LOGIN_LABEL },
  ] as const;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeNav();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeNav]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            animate={{ opacity: 1 }}
            className="bg-background/60 fixed inset-0 z-40 backdrop-blur-sm"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: EASE }}
            onClick={closeNav}
          />
          <motion.aside
            animate={{ x: 0 }}
            className="bg-sidebar border-sidebar-border fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r shadow-xl"
            exit={{ x: "-100%" }}
            initial={{ x: "-100%" }}
            transition={{ duration: 0.25, ease: EASE }}
          >
            <div className="border-sidebar-border flex h-14 items-center justify-between border-b px-3">
              <div className="flex items-center gap-3">
                <LogoIcon size="sm" />
                <Logotype glow className="whitespace-nowrap" />
              </div>
              <button
                aria-label={CLOSE_LABEL}
                className="hover:bg-sidebar-accent text-muted-foreground hover:text-foreground flex h-8 w-8 cursor-pointer items-center justify-center rounded-md transition-colors"
                type="button"
                onClick={closeNav}
              >
                <X size={18} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-2 py-3">
              <ul className="flex flex-col gap-0.5">
                {navItems.map(({ href, icon: Icon, label }) => {
                  const active =
                    href === "/" ? pathname === "/" : pathname.startsWith(href);
                  return (
                    <li key={href}>
                      <Link
                        href={href}
                        className={cn(
                          "flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                          active
                            ? "text-foreground bg-sidebar-accent glow-surface font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent",
                        )}
                        onClick={closeNav}
                      >
                        <Icon className="shrink-0" size={18} />
                        <span>{label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
